// src/useSidebarCollapsed.js - one collapsed/expanded state, shared
import { useState, useEffect, useCallback } from 'react';

const KEY = 'sidebar_collapsed';
const EVENT = 'sidebar-collapsed-changed';

export const SIDEBAR_WIDTH = 240;
export const SIDEBAR_WIDTH_COLLAPSED = 68;

/**
 * Whether the sidebar is collapsed, readable from anywhere.
 *
 * Two components need this and they are not parent and child: the Sidebar draws
 * itself, and App.js sets the header's left margin, which was hardcoded to
 * 240px and would otherwise leave a gap the width of the sidebar it no longer
 * has. A window event keeps them in step without threading state through the
 * tree or adding a provider for one boolean.
 *
 * Remembered per browser, because a person who collapses it wants it collapsed
 * tomorrow as well.
 */
export default function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(KEY) === 'true';
    } catch {
      // Private windows and blocked site data both throw here. Starting
      // expanded is the safe answer: the labels are the discoverable state.
      return false;
    }
  });

  useEffect(() => {
    const onChange = (e) => setCollapsed(e.detail);
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(KEY, String(next));
      } catch {
        /* the toggle still works for this session */
      }
      window.dispatchEvent(new CustomEvent(EVENT, { detail: next }));
      return next;
    });
  }, []);

  return { collapsed, toggle, width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH };
}
